/**
 * Application-wide constants
 */

/**
 * API Configuration
 *
 * API_BASE_URL defaults to empty string for same-origin requests.
 * - In development: Vite proxy forwards /api requests to localhost:8787
 * - In production: Cloudflare Worker serves both frontend and API on same origin
 *
 * Override with VITE_API_URL environment variable if needed (e.g., for custom deployments)
 */
export const API_BASE_URL =
  (import.meta as { env?: Record<string, string> }).env?.VITE_API_URL || "";
export const API_TIMEOUT = 30000; // 30 seconds

/**
 * Application Metadata
 */
export const APP_NAME = "DeTrust";
export const APP_DESCRIPTION = "Solana Web3 Application";
export const APP_URL =
  typeof window !== "undefined"
    ? window.location.origin
    : "https://detrust.ai";
export const APP_ICON_URL = `${typeof window !== "undefined" ? window.location.origin : "https://detrust.ai"}/logo.png`;

/**
 * Solana RPC Configuration
 *
 * The frontend uses the backend RPC proxy at /api/rpc for all read operations.
 * This provides:
 * - Rate limiting and request optimization
 * - Caching at the edge
 * - Consistent error handling
 * - Reduced direct RPC costs
 *
 * Read operations (getBalance, getAccountInfo, getParsedTokenAccountsByOwner, etc.)
 * use the proxy endpoint via Connection('/api/rpc').
 *
 * Write operations (sendTransaction, sendRawTransaction) go through the relay API
 * at /api/relay/* endpoints for proper transaction handling and monitoring.
 */
const SOLANA_RPC_ENDPOINT_PATH = "/api/rpc";

/**
 * Get the full Solana RPC endpoint URL
 * Converts relative path to absolute URL for @solana/web3.js Connection class
 *
 * In development:
 * - Frontend runs on localhost:5173
 * - Backend runs on localhost:8787
 * - Vite proxy forwards /api/* to http://localhost:8787/api/*
 *
 * In production:
 * - Both frontend and backend run on the same origin
 * - Uses window.location.origin + /api/rpc
 *
 * @returns Full URL to the RPC endpoint (e.g., "http://localhost:5173/api/rpc")
 */
export const getSolanaRpcEndpoint = (): string => {
  const endpoint = SOLANA_RPC_ENDPOINT_PATH;

  // If it's already a full URL, use as-is
  if (endpoint.startsWith("http://") || endpoint.startsWith("https://")) {
    return endpoint;
  }

  // In browser environment, construct full URL
  if (typeof window !== "undefined") {
    // In development, Vite proxy handles /api requests on the same origin
    // In production, frontend and backend are on the same origin
    return `${window.location.origin}${endpoint}`;
  }

  // Fallback for SSR or non-browser environments (shouldn't happen in this app)
  return endpoint;
};

/**
 * Export the path for backward compatibility
 */
export const SOLANA_RPC_ENDPOINT = SOLANA_RPC_ENDPOINT_PATH;

/**
 * Solana Network
 */
export const SOLANA_NETWORK =
  (import.meta as { env?: Record<string, string> }).env?.VITE_SOLANA_NETWORK ||
  "mainnet-beta";

/**
 * Solana Constants
 */
export const LAMPORTS_PER_SOL = 1_000_000_000;
export const DEFAULT_TOKEN_DECIMALS = 9; // Standard for Solana SPL tokens

/**
 * Transaction Settings
 */
export const TRANSACTION_CONFIRMATION_COMMITMENT = "confirmed" as const;
export const TRANSACTION_TIMEOUT = 60000; // 60 seconds

/**
 * Common Solana Token Mints (Mainnet)
 */
export const COMMON_TOKEN_MINTS = {
  USDC: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
  USDT: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB",
  SOL: "So11111111111111111111111111111111111111112", // Wrapped SOL
} as const;

/**
 * Pagination
 */
export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;

/**
 * Cache TTL (Time To Live) in milliseconds
 */
export const CACHE_TTL = {
  PRICES: 30 * 1000, // 30 seconds
  USER_DATA: 60 * 1000, // 1 minute
} as const;
